"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { MeResponse } from "@/lib/types";
import { StatusScreen } from "@/components/status-screen";

type MeStore = {
  me: MeResponse;
  refreshMe: () => Promise<void>;
  markSeen: (tab: "homework" | "votes") => Promise<void>;
};

const MeContext = createContext<MeStore | null>(null);

export function MeProvider({
  me: initial,
  children,
}: {
  me: MeResponse;
  children: React.ReactNode;
}) {
  const [me, setMe] = useState(initial);
  const [checked, setChecked] = useState(!initial.user.isGuest);

  const refreshMe = useCallback(async () => {
    const next = await api<MeResponse>("/api/me");
    setMe(next);
  }, []);

  const markSeen = useCallback(async (tab: "homework" | "votes") => {
    const next = await api<MeResponse>("/api/me/seen", {
      method: "POST",
      body: JSON.stringify({ [tab]: true }),
    });
    setMe(next);
  }, []);

  useEffect(() => {
    void api<MeResponse>("/api/me")
      .then(setMe)
      .catch(() => undefined)
      .finally(() => setChecked(true));
  }, []);

  useEffect(() => {
    if (me.user.isGuest || me.user.role === "pending") return;

    function onVisible() {
      if (document.visibilityState === "visible") void refreshMe().catch(() => undefined);
    }

    document.addEventListener("visibilitychange", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [me.user.isGuest, me.user.role, refreshMe]);

  if (!checked) {
    return (
      <div className="app-frame">
        <StatusScreen loading title="Заходимо" text="Перевіряємо Telegram." />
      </div>
    );
  }

  return <MeContext.Provider value={{ me, refreshMe, markSeen }}>{children}</MeContext.Provider>;
}

export function useMeContext(): MeResponse {
  const store = useContext(MeContext);
  if (!store) {
    throw new Error("Профіль класу ще не завантажено.");
  }
  return store.me;
}

export function useMeActions() {
  const store = useContext(MeContext);
  if (!store) {
    throw new Error("Профіль класу ще не завантажено.");
  }
  return { refreshMe: store.refreshMe, markSeen: store.markSeen };
}
